import { useAppDispatch, useAppSelector } from '@/app/reduxHooks'
import { ROUTES } from '@/app/routes'
import {
  fetchRegisteredSourceDataSources,
  fetchRegisteredTargetDataSources,
} from '@/features/adp/api/adpDataSources.api'
import { emptyBucketedConnectors } from '@/features/adp/api/dataSourcePaletteBuckets'
import {
  fetchDataFlowRecordById,
  killEtlJob,
  runImmediateEtlJob,
  updateDataFlow,
} from '@/features/adp/api/pipelineLists.api'
import { DataSourcePalette, useDataSourcePaletteState } from '@/features/adp/components'
import {
  AdpPipelineFlowCanvas,
  DRAG_MIME,
  type DragPayload,
} from '@/features/adp/components/flow'
import type { PipelineGraphBridge } from '@/features/adp/components/flow/pipelineGraphBridge.types'
import { GuidedTour } from '@/features/adp/components/guided'
import { PipelineActionsMenu, PipelineMoreMenu } from '@/features/adp/components/menus'
import { PipelinesListView, RunHistoryPanel } from '@/features/adp/components/panels'
import {
  ChooseDataDestinationSheet,
  ChooseDataSourceSheet,
  ConfigureDataDestinationSheet,
  ConfigureDataSourceSheet,
  ConfigureTransformationSheet,
  EditPipelineModal,
  JoinDetailsSheet,
  PipelineDetailsSheet,
} from '@/features/adp/components/sheets'
import {
  getTransformationIconSrc,
  INTEGRATION_ITEMS,
} from '@/features/adp/constants/adpPalette.constants'
import { ChooseDataSourceContext } from '@/features/adp/context'
import { useAdpPaletteDataSources } from '@/features/adp/hooks/useAdpPaletteDataSources'
import { useInitializePipelineDataFlow } from '@/features/adp/hooks/useInitializePipelineDataFlow'
import { useJobMonitoringWebSocket } from '@/features/adp/hooks/useJobMonitoringWebSocket'
import {
  mapToRegisteredPickerItem,
  useRegisteredSourceDataSources,
  useRegisteredTargetDataSources,
  type RegisteredSourcePickerItem,
} from '@/features/adp/hooks/useRegisteredDataSources'
import { useAllTransformations } from '@/features/adp/hooks/UseTransformations'
import {
  resetPipelineState,
  setPipelineState,
  setRunEnabled,
  setStatus,
} from '@/features/adp/state/pipelineSlice'
import { registerDeletePipelineCallback } from '@/features/adp/hooks/useDeletePipelineCallback'
import {
  clearAdpPipelineSession,
  getSourceConfigDraft,
  getSelectedRegisteredSource,
  readAdpPipelineSession,
  removeSelectedRegisteredSource,
  upsertPipelineSessionId,
  upsertSelectedRegisteredSource,
  type AdpPipelineSessionV1,
} from '@/features/adp/utils/adpPipelineSessionStorage'
import { closeOverlay, openOverlay as openGlobalOverlay } from '@/features/adp/state/overlaySlice'
import {
  buildPipelineDefinition,
  createEmptyPipelineDefinitionSession,
  parsePipelineDefinition,
} from '@/features/adp/utils/pipelineDefinition'
import { getUpstreamSourceNodeIds } from '@/features/adp/utils/pipelineGraphUtils'
import Breadcrumb from '@/shared/components/Breadcrumb/Breadcrumb'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/ui/badge'
import { Button } from '@/ui/button'
import { Input } from '@/ui/input'
import { MaterialIcon } from '@/ui/material-icon'
import { Separator } from '@/ui/separator'
import { Skeleton } from '@/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/tabs'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/tooltip'
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Edge, Node } from 'reactflow'
import { toast } from 'sonner'
import styles from './AdpPipelinePage.module.scss'
import { normalizeInstance, shouldSkipRegisteredDestinationPicker, toTitleCase } from './helpers'

type PaletteItem = {
  id: string
  label: string
  icon: string
  iconSrc?: string
}

type AssetGroup = {
  id: string
  label: string
  icon: string
  items: PaletteItem[]
}

type PendingAssetDrop = {
  payload: DragPayload
  position: { x: number; y: number }
}

const ASSET_GROUPS: AssetGroup[] = [
  {
    id: 'sources',
    label: 'Data Sources',
    icon: 'database',
    items: [] as PaletteItem[],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: 'hub',
    items: [] as PaletteItem[],
  },
  {
    id: 'transformations',
    label: 'Transformations',
    icon: 'transform',
    items: [] as PaletteItem[],
  },
  {
    id: 'destinations',
    label: 'Data Destinations',
    icon: 'cloud_upload',
    items: [] as PaletteItem[],
  },
]

function onPaletteDragStart(e: DragEvent, payload: DragPayload) {
  e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload))
  e.dataTransfer.effectAllowed = 'move'
}

export default function AdpPipelinePage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [assetSearchQuery, setAssetSearchQuery] = useState('');
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const sourcePaletteUi = useDataSourcePaletteState(true)
  const destinationPaletteUi = useDataSourcePaletteState(true)
  const paletteSourceDataSourcesQuery = useAdpPaletteDataSources('source')
  const paletteDestinationDataSourcesQuery = useAdpPaletteDataSources('destination')
  const paletteSourceConnectors =
    paletteSourceDataSourcesQuery.data ?? emptyBucketedConnectors()
  const paletteDestinationConnectors =
    paletteDestinationDataSourcesQuery.data ?? emptyBucketedConnectors()

  const {
    transformations,
    isLoading: transformationsLoading,
    // error: transformationsError,
  } = useAllTransformations()

  const transformationPaletteItems = useMemo(() => {
    return transformations.map(transform => {
      const normalizedName = transform.name.trim().toLowerCase();
      const connectorId = normalizedName.replaceAll(/[^a-z0-9]/g, '');
      const iconSrc = getTransformationIconSrc(normalizedName);

      return {
        id: connectorId,
        label: transform.name,
        icon: 'transform',
        iconSrc,
      }
    })
  }, [transformations]);

  const [integrationsMergeOpen, setIntegrationsMergeOpen] = useState(true)
  const [transformationsLibraryOpen, setTransformationsLibraryOpen] = useState(true)
  const [activeTab, setActiveTab] = useState<'assets' | 'pipelines'>('assets')
  const [editPipelineOpen, setEditPipelineOpen] = useState(false)
  const [pipelineDetailsOpen, setPipelineDetailsOpen] = useState(false)
  const [runHistoryOpen, setRunHistoryOpen] = useState(false)
  const [chooseDataSourceOpen, setChooseDataSourceOpen] = useState(false)
  const [chooseDataSourceNodeId, setChooseDataSourceNodeId] = useState<string | null>(null)
  const [chooseDataSourceAllowedInstance, setChooseDataSourceAllowedInstance] = useState<string | null>(null)
  const [chooseDataSourceDateFilter, setChooseDataSourceDateFilter] = useState<string | null>(null)
  const [chooseDataSourceSearchQuery, setChooseDataSourceSearchQuery] = useState('')
  const [chooseDataSourceAssetKind, setChooseDataSourceAssetKind] = useState<
    'source' | 'destination'
  >('source')
  const [dataSourceSheetStep, setDataSourceSheetStep] = useState<'choose' | 'configure'>('choose')
  const [selectedRegisteredSource, setSelectedRegisteredSource] =
    useState<RegisteredSourcePickerItem | null>(null)
  const [integrationSettings, setIntegrationSettings] = useState<{
    nodeId: string
    connectorId: string
  } | null>(null)
  const [transformationSettings, setTransformationSettings] = useState<{
    nodeId: string
    connectorId: string
  } | null>(null)
  const [pendingAssetDrop, setPendingAssetDrop] = useState<PendingAssetDrop | null>(null)
  const [graphSnapshot, setGraphSnapshot] = useState<{ nodes: Node[]; edges: Edge[] }>({
    nodes: [],
    edges: [],
  });
  const [hydrationSnapshot, setHydrationSnapshot] = useState<{
    nodes: Node[]
    edges: Edge[]
    session: AdpPipelineSessionV1
  } | null>(null)
  const [hydrationKey, setHydrationKey] = useState(0)
  const { dataFlowId, sessionId, pipelineName, isRunEnabled, status: pipelineStatus } = useAppSelector(
    state => state.pipeline
  );
  const hasPipelineName = Boolean(pipelineName?.trim())
  const hasValidDataFlowBootstrap = dataFlowId !== null && Boolean(sessionId)
  const isInitializingPipeline = pipelineStatus === 'initializing' && !hasValidDataFlowBootstrap
  const isRunningPipeline = pipelineStatus === 'initializing' && hasValidDataFlowBootstrap
  const isPipelineMarkedRunning = pipelineStatus === 'running'

  const [isGraphBridgeReady, setIsGraphBridgeReady] = useState(false)
  const debouncedChooseDataSourceSearchQuery = useDebounce(chooseDataSourceSearchQuery, 500)
  const pipelineGraphBridgeRef = useRef<PipelineGraphBridge | null>(null)

  const hydrateRegisteredSourceFromBackend = useCallback(
    async ({
      preferredId,
      dataSourceName,
      dataSourceInstance,
    }: {
      preferredId?: string | number | null
      dataSourceName?: string | null
      dataSourceInstance?: string | null
    }): Promise<RegisteredSourcePickerItem | null> => {
      const instance = dataSourceInstance?.trim() || undefined
      const preferredIdNumber = Number(preferredId)
      const normalizedName = dataSourceName?.trim().toLowerCase() || ''

      try {
        const registered = await fetchRegisteredSourceDataSources({
          date_input: 'last_7_days',
          allowed_instances: instance,
        })

        const exactById = Number.isFinite(preferredIdNumber)
          ? registered.find(
            item => Number(item.source_data_source_id) === preferredIdNumber
          )
          : null
        const exactByName = normalizedName
          ? registered.find(item => item.data_source_name?.trim().toLowerCase() === normalizedName)
          : null
        const relaxedByInstanceAndName = normalizedName
          ? registered.find(item => {
            const itemInstance = normalizeInstance(item.data_source_instance || item.data_source_type)
            const wantedInstance = normalizeInstance(dataSourceInstance)
            if (wantedInstance && itemInstance !== wantedInstance) return false
            return item.data_source_name?.trim().toLowerCase() === normalizedName
          })
          : null

        const matched = exactById ?? exactByName ?? relaxedByInstanceAndName ?? null
        return matched ? mapToRegisteredPickerItem(matched) : null
      } catch {
        return null
      }
    },
    []
  )

  const hydrateRegisteredTargetFromBackend = useCallback(
    async ({
      preferredId,
      dataSourceName,
      dataSourceInstance,
    }: {
      preferredId?: string | number | null
      dataSourceName?: string | null
      dataSourceInstance?: string | null
    }): Promise<RegisteredSourcePickerItem | null> => {
      const instance = dataSourceInstance?.trim() || undefined
      const preferredIdNumber = Number(preferredId)
      const normalizedName = dataSourceName?.trim().toLowerCase() || ''

      try {
        const registered = await fetchRegisteredTargetDataSources({
          date_input: 'last_7_days',
          allowed_instances: instance,
        })

        const exactById = Number.isFinite(preferredIdNumber)
          ? registered.find(
            item => Number(item.target_data_source_id) === preferredIdNumber
          )
          : null
        const exactByName = normalizedName
          ? registered.find(item => item.data_source_name?.trim().toLowerCase() === normalizedName)
          : null
        const relaxedByInstanceAndName = normalizedName
          ? registered.find(item => {
            const itemInstance = normalizeInstance(item.data_source_instance || item.data_source_type)
            const wantedInstance = normalizeInstance(dataSourceInstance)
            if (wantedInstance && itemInstance !== wantedInstance) return false
            return item.data_source_name?.trim().toLowerCase() === normalizedName
          })
          : null

        const matched = exactById ?? exactByName ?? relaxedByInstanceAndName ?? null
        return matched ? mapToRegisteredPickerItem(matched) : null
      } catch {
        return null
      }
    },
    []
  )

  useEffect(() => {
    if (sessionId) {
      upsertPipelineSessionId(sessionId)
    }
  }, [sessionId])

  // ── Job Monitoring WebSocket ─────────────────────────────────────────────────
  const {
    jobStatus,
    liveStatuses,
    liveMessages,
    startListening: startJobMonitoring,
    stopListening: stopJobMonitoring,
  } = useJobMonitoringWebSocket({
    dataFlowId,
    onMessage: (msg) => {
      console.log('[JobMonitoring] message:', msg)
    },
    onTerminal: (status) => {
      if (status === 'finished') {
        dispatch(setStatus('completed'))
        toast.success('Pipeline finished successfully.')
        return
      }
      if (status === 'stopped') {
        dispatch(setStatus('failed'))
        toast.info('Pipeline was stopped.')
        return
      }
      toast.error('Pipeline failed. Please check run details.')
      dispatch(setStatus('idle'))
    },
  })

  const isPipelineInTerminalState =
    jobStatus === 'finished' || jobStatus === 'failed' || jobStatus === 'stopped'
  const isPipelineActive = isPipelineMarkedRunning && !isPipelineInTerminalState

  const initializePipelineQuery = useInitializePipelineDataFlow()

  // Automatically start listening for any pipeline status updates when the page mounts
  useEffect(() => {
    startJobMonitoring()
  }, [startJobMonitoring])

  const handleGraphChange = useCallback((snap: { nodes: Node[]; edges: Edge[] }) => {
    setGraphSnapshot(snap)
  }, [])
  const hasCanvasNodes = graphSnapshot.nodes.length > 0

  const closeAllPanelsAndSheets = useCallback(() => {
    setEditPipelineOpen(false)
    setPipelineDetailsOpen(false)
    setRunHistoryOpen(false)
    setPendingAssetDrop(null)
    setChooseDataSourceOpen(false)
    setChooseDataSourceNodeId(null)
    setChooseDataSourceAllowedInstance(null)
    setChooseDataSourceDateFilter(null)
    setChooseDataSourceSearchQuery('')
    setChooseDataSourceAssetKind('source')
    setDataSourceSheetStep('choose')
    setSelectedRegisteredSource(null)
    setIntegrationSettings(null)
    setTransformationSettings(null)
    dispatch(closeOverlay())
  }, [dispatch])

  const handlePipelineGraphBridgeReady = useCallback((bridge: PipelineGraphBridge) => {
    pipelineGraphBridgeRef.current = bridge
    setIsGraphBridgeReady(true)
  }, [])

  const handleClearCanvas = useCallback(() => {
    if (!hasCanvasNodes) return
    pipelineGraphBridgeRef.current?.clearAllNodes()
    toast.success('Canvas cleared successfully')
  }, [hasCanvasNodes, toast])

  const handleNewPipeline = useCallback(async () => {
    stopJobMonitoring()
    clearAdpPipelineSession()
    closeAllPanelsAndSheets()
    dispatch(resetPipelineState())
    setGraphSnapshot({ nodes: [], edges: [] })
    setHydrationSnapshot({
      nodes: [],
      edges: [],
      session: createEmptyPipelineDefinitionSession(),
    })
    setHydrationKey(prev => prev + 1)
    setAssetSearchQuery('')
    setActiveTab('assets')
    await initializePipelineQuery.refetch()
  }, [closeAllPanelsAndSheets, dispatch, initializePipelineQuery, stopJobMonitoring])

  const handleLockPipeline = useCallback(() => {
    if (!hasCanvasNodes) return
    pipelineGraphBridgeRef.current?.toggleCanvasLock()
  }, [hasCanvasNodes])

  const handleDeletePipeline = useCallback(() => {
    dispatch(
      openGlobalOverlay({
        type: 'delete-pipeline-dialog',
        data: {
          pipelineId: dataFlowId == null ? null : String(dataFlowId),
          pipelineName: pipelineName ?? null,
          source: 'canvas',
        },
      })
    )
  }, [dataFlowId, dispatch, pipelineName])

  useEffect(() => {
    registerDeletePipelineCallback(() => {
      pipelineGraphBridgeRef.current?.clearAllNodes()
      clearAdpPipelineSession()
      closeAllPanelsAndSheets()
      dispatch(resetPipelineState())
      dispatch(setStatus('idle'))
      toast.success('Pipeline deleted and canvas cleared.')
    })
    return () => registerDeletePipelineCallback(null)
  }, [closeAllPanelsAndSheets, dispatch, toast])

  const hasIncomingEdge = useCallback(
    (nodeId: string) => graphSnapshot.edges.some(edge => edge.target === nodeId),
    [graphSnapshot.edges]
  )

  const toggleGroup = (id: string) => {
    setOpenGroups(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  const closeIntegrationSettings = useCallback(() => {
    setIntegrationSettings(null)
  }, [])

  const closeTransformationSettings = useCallback(() => {
    setTransformationSettings(null)
  }, [])

  const requirePipelineSetup = useCallback((pendingDrop?: PendingAssetDrop) => {
    if (pendingDrop) {
      setPendingAssetDrop(pendingDrop)
    }
    if (isInitializingPipeline) {
      toast.info('Pipeline is being prepared. Please wait a moment.')
      return
    }
    toast.info('Click Edit next to the pipeline name and save to create the data flow, then you can drag assets.')
  }, [isInitializingPipeline, toast])

  useEffect(() => {
    clearAdpPipelineSession()
    dispatch(resetPipelineState())
    dispatch(setStatus('idle'))
    setEditPipelineOpen(false)
    setPendingAssetDrop(null)
  }, [dispatch])

  useEffect(() => {
    if (!pendingAssetDrop || !hasValidDataFlowBootstrap || !isGraphBridgeReady) return

    pipelineGraphBridgeRef.current?.addAssetNode({
      payload: pendingAssetDrop.payload,
      position: pendingAssetDrop.position,
    })
    setPendingAssetDrop(null)
  }, [hasValidDataFlowBootstrap, isGraphBridgeReady, pendingAssetDrop])

  const openChooseDataSource = useCallback(
    (nodeId: string, connectorId: string | undefined, groupId?: string) => {
      if (!hasValidDataFlowBootstrap) {
        requirePipelineSetup()
        return
      }
      if (groupId !== 'sources' && !hasIncomingEdge(nodeId)) {
        toast.info('Connect this node to a previous step before configuring it.')
        return
      }

      setIntegrationSettings(null)
      setChooseDataSourceNodeId(nodeId)
      setChooseDataSourceAllowedInstance(connectorId?.trim() || null)
      setChooseDataSourceDateFilter(null)
      setChooseDataSourceSearchQuery('')
      setChooseDataSourceAssetKind(groupId === 'destinations' ? 'destination' : 'source')
      setChooseDataSourceOpen(true)
      setPipelineDetailsOpen(false)
      setRunHistoryOpen(false)

      if (shouldSkipRegisteredDestinationPicker(connectorId, groupId)) {
        setDataSourceSheetStep('configure')
        setSelectedRegisteredSource(null)
        if (nodeId) {
          removeSelectedRegisteredSource(nodeId)
        }
        return;
      }

      const configuredAsset = readAdpPipelineSession().assets[nodeId]
      const sourceDraft = getSourceConfigDraft(nodeId)

      // Check if user has previously selected a registered source/destination for this node
      const previousSelection = getSelectedRegisteredSource(nodeId)
      if (previousSelection) {
        const expectedKind = groupId === 'destinations' ? 'destination' : 'source'
        const hasConfiguredAsset = configuredAsset?.assetKind === expectedKind

        // Destination flow should reopen in configured mode only after a real save.
        // A temporary list selection must not skip the destination picker on next open.
        if (groupId === 'destinations' && !hasConfiguredAsset) {
          setDataSourceSheetStep('choose')
          setSelectedRegisteredSource(null)
          removeSelectedRegisteredSource(nodeId)
          return
        }

        // Convert stored selection back to RegisteredSourcePickerItem format
        const registeredSourceItem: RegisteredSourcePickerItem = {
          id: previousSelection.registeredSourceId,
          kind: previousSelection.dataSourceInstance,
          connectionName: previousSelection.registeredSourceName,
          dataSourceInstance: previousSelection.dataSourceInstance,
          dataSourceType: previousSelection.dataSourceInstance,
          dataSourceInstanceLabel:
            previousSelection.dataSourceInstance.substring(0, 1).toUpperCase() +
            previousSelection.dataSourceInstance.substring(1).toLowerCase(),
          dataSourceTypeFormatted: previousSelection.dataSourceInstance.toLowerCase().replaceAll(' ', ''),
          // Set empty defaults for fields we don't store
          image: '',
          username: '',
          password: '',
          databaseName: '',
          endpoint: '',
        }
        setSelectedRegisteredSource(registeredSourceItem)
        setDataSourceSheetStep('configure')
        void (async () => {
          const hydrated =
            groupId === 'destinations'
              ? await hydrateRegisteredTargetFromBackend({
                preferredId: previousSelection.registeredSourceId,
                dataSourceName: previousSelection.registeredSourceName,
                dataSourceInstance: previousSelection.dataSourceInstance,
              })
              : await hydrateRegisteredSourceFromBackend({
                preferredId: previousSelection.registeredSourceId,
                dataSourceName: previousSelection.registeredSourceName,
                dataSourceInstance: previousSelection.dataSourceInstance,
              })
          if (hydrated) setSelectedRegisteredSource(hydrated)
        })()
      } else {
        const expectedKind = groupId === 'destinations' ? 'destination' : 'source'
        const hasSavedSourceDraft =
          expectedKind === 'source' &&
          sourceDraft &&
          (Object.keys(sourceDraft.form ?? {}).length > 0 ||
            Object.keys(sourceDraft.responseSelections ?? {}).length > 0 ||
            (sourceDraft.selectedTableName?.trim()?.length ?? 0) > 0 ||
            (sourceDraft.discoveredTables?.length ?? 0) > 0 ||
            (sourceDraft.tableColumns?.length ?? 0) > 0 ||
            Object.keys(sourceDraft.columnConfigs ?? {}).length > 0 ||
            sourceDraft.explorerOpen === true ||
            sourceDraft.profilingOpen === true)
        if (configuredAsset?.assetKind === expectedKind || hasSavedSourceDraft) {
          // Reopen configured details directly. Only explicit registered selections
          // should drive registered-mode UI; plain configured assets should stay
          // in dynamic/new-source mode so users can see and edit entered form fields.
          setSelectedRegisteredSource(null)
          setDataSourceSheetStep('configure')
        } else {
          // No previous selection, start fresh
          setDataSourceSheetStep('choose')
          setSelectedRegisteredSource(null)
        }
      }
    },
    [
      hasIncomingEdge,
      hasValidDataFlowBootstrap,
      hydrateRegisteredSourceFromBackend,
      hydrateRegisteredTargetFromBackend,
      requirePipelineSetup,
      toast,
    ]
  )

  const openIntegrationSettings = useCallback((nodeId: string, connectorId: string | undefined) => {
    if (!hasValidDataFlowBootstrap) {
      requirePipelineSetup()
      return
    }
    if (!hasIncomingEdge(nodeId)) {
      toast.info('Connect this node to a source before configuring the integration.')
      return
    }

    setChooseDataSourceOpen(false)
    setChooseDataSourceNodeId(null)
    setDataSourceSheetStep('choose')
    setPipelineDetailsOpen(false)
    setRunHistoryOpen(false)
    setTransformationSettings(null)
    setIntegrationSettings({
      nodeId,
      connectorId: connectorId ?? 'join',
    })
  }, [hasIncomingEdge, hasValidDataFlowBootstrap, requirePipelineSetup, toast])

  const openTransformationSettings = useCallback((nodeId: string, connectorId: string | undefined) => {
    if (!hasValidDataFlowBootstrap) {
      requirePipelineSetup()
      return
    }
    if (!hasIncomingEdge(nodeId)) {
      toast.info('Connect this node to a previous step before configuring the transformation.')
      return
    }

    setChooseDataSourceOpen(false)
    setChooseDataSourceNodeId(null)
    setDataSourceSheetStep('choose')
    setPipelineDetailsOpen(false)
    setRunHistoryOpen(false)
    setIntegrationSettings(null)
    setTransformationSettings({
      nodeId,
      connectorId: connectorId ?? 'split',
    })
  }, [hasIncomingEdge, hasValidDataFlowBootstrap, requirePipelineSetup, toast])

  const closeDataSourceFlow = useCallback(() => {
    setChooseDataSourceOpen(false)
    setChooseDataSourceNodeId(null)
    setChooseDataSourceAllowedInstance(null)
    setChooseDataSourceDateFilter(null)
    setChooseDataSourceSearchQuery('')
    setChooseDataSourceAssetKind('source')
    setDataSourceSheetStep('choose')
    setSelectedRegisteredSource(null)
    closeIntegrationSettings()
    closeTransformationSettings()
  }, [closeIntegrationSettings, closeTransformationSettings])

  const saveDestinationChanges = useCallback(async () => {
    if (!dataFlowId || !sessionId) {
      requirePipelineSetup()
      return
    }
    const sessionSnapshot = readAdpPipelineSession()
    const pipelineDefinition = buildPipelineDefinition({
      dataFlowId,
      sessionId,
      nodes: graphSnapshot.nodes,
      edges: graphSnapshot.edges,
      session: sessionSnapshot,
    })
    await updateDataFlow(dataFlowId, {
      session_id: sessionId,
      transformation_definition: JSON.stringify(sessionSnapshot.fullOperationJson),
      pipeline_definition: JSON.stringify(pipelineDefinition),
    })
    toast.success('Destination changes saved to data flow.')
  }, [dataFlowId, graphSnapshot.edges, graphSnapshot.nodes, requirePipelineSetup, sessionId, toast])

  const handleOpenPipelineFromList = useCallback(async (pipelineId: string) => {
    const nextDataFlowId = Number(pipelineId)
    if (!Number.isFinite(nextDataFlowId) || nextDataFlowId <= 0) {
      toast.error('Invalid pipeline id selected.')
      return
    }

    try {
      const record = await fetchDataFlowRecordById(nextDataFlowId)
      const parsedDefinition = parsePipelineDefinition(record.pipeline_definition)
      const fallbackSession = createEmptyPipelineDefinitionSession()
      const graph = parsedDefinition?.graph ?? { nodes: [], edges: [] }
      const session = parsedDefinition?.session ?? fallbackSession
      const resolvedSessionId =
        record.session_id?.trim() ||
        parsedDefinition?.sessionId?.trim() ||
        session.sessionId?.trim() ||
        ''

      dispatch(
        setPipelineState({
          dataFlowId: record.data_flow_id ?? nextDataFlowId,
          sessionId: resolvedSessionId || null,
          pipelineName: record.data_flow_name ?? '',
          pipelineDescription: record.description ?? '',
        })
      )

      setHydrationSnapshot({
        nodes: graph.nodes,
        edges: graph.edges,
        session,
      })
      setHydrationKey(prev => prev + 1)
      setGraphSnapshot({ nodes: graph.nodes, edges: graph.edges })
      setRunEnabled(session.columnMapping.length > 0)
      dispatch(setStatus('idle'))
      setEditPipelineOpen(false)
      setPipelineDetailsOpen(false)
      setRunHistoryOpen(false)
      closeDataSourceFlow()
      setActiveTab('assets')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to open pipeline.')
    }
  }, [closeDataSourceFlow, dispatch, toast])

  const handleRunPipeline = useCallback(async () => {
    const activeSessionId = sessionId?.trim()
    if (!activeSessionId) {
      requirePipelineSetup()
      return;
    }
    if (!isRunEnabled) {
      return toast.info(`Pipeline setup isn't complete. Finish configuring all steps and save before running.`);
    }

    dispatch(setStatus('initializing'));
    try {
      await runImmediateEtlJob(activeSessionId);
      dispatch(setStatus('running'));
      toast.success('Pipeline started successfully.');
      // Connect WebSocket to monitor job progress
      startJobMonitoring()
    } catch (err) {
      dispatch(setStatus('idle'))
      toast.error(err instanceof Error ? err.message : 'Failed to start pipeline.')
    }
  }, [dispatch, isRunEnabled, requirePipelineSetup, sessionId, startJobMonitoring, toast])

  const handleStopPipeline = useCallback(async () => {
    const activeSessionId = sessionId?.trim()
    if (!activeSessionId) {
      requirePipelineSetup()
      return
    }

    try {
      await killEtlJob(activeSessionId)
      dispatch(setStatus('idle'))
      stopJobMonitoring()
      toast.success('Pipeline stop requested.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to stop pipeline run.')
    }
  }, [dispatch, killEtlJob, requirePipelineSetup, sessionId, stopJobMonitoring, toast])

  const handleRunImmediateList = useCallback(async (sessionId: string) => {
    try {
      await runImmediateEtlJob(sessionId)
      toast.success('Pipeline triggered successfully.')
      startJobMonitoring()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to trigger pipeline.')
    }
  }, [startJobMonitoring, toast])

  const handleStopPipelineFromList = useCallback(async (sessionId: string) => {
    const trimmedSessionId = sessionId?.trim()
    if (!trimmedSessionId) {
      toast.error('Invalid session ID for stopping pipeline.')
      return
    }

    try {
      await killEtlJob(trimmedSessionId)
      toast.success('Pipeline stop requested.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to stop pipeline.')
    }
  }, [killEtlJob, toast])

  const registeredSourceDataSourcesQuery = useRegisteredSourceDataSources(
    chooseDataSourceAllowedInstance,
    chooseDataSourceDateFilter,
    debouncedChooseDataSourceSearchQuery.trim() || null,
    chooseDataSourceOpen &&
    dataSourceSheetStep === 'choose' &&
    chooseDataSourceAssetKind === 'source'
  );
  const registeredTargetDataSourcesQuery = useRegisteredTargetDataSources(
    chooseDataSourceAllowedInstance,
    chooseDataSourceDateFilter,
    debouncedChooseDataSourceSearchQuery.trim() || null,
    chooseDataSourceOpen &&
    dataSourceSheetStep === 'choose' &&
    chooseDataSourceAssetKind === 'destination'
  );

  const handleSelectExistingSource = useCallback(
    (source: RegisteredSourcePickerItem, configuringNodeId: string | null) => {
      setSelectedRegisteredSource(source)
      setDataSourceSheetStep('configure')

      // Store the selected registered source in session storage for persistence
      if (configuringNodeId) {
        upsertSelectedRegisteredSource(configuringNodeId, {
          id: source.id,
          name: source.connectionName,
          dataSourceName: source.connectionName,
          dataSourceInstance: source.dataSourceInstance,
        })
      }
    },
    []
  )

  const chooseDataSourceContextValue = useMemo(
    () => ({ openChooseDataSource, openIntegrationSettings, openTransformationSettings }),
    [openChooseDataSource, openIntegrationSettings, openTransformationSettings]
  )

  const integrationUpstreamSourceIds = useMemo(() => {
    if (!integrationSettings) return [] as string[]
    return getUpstreamSourceNodeIds(
      integrationSettings.nodeId,
      graphSnapshot.nodes,
      graphSnapshot.edges
    )
  }, [integrationSettings, graphSnapshot])

  const filteredPaletteSourceConnectors = useMemo(() => {
    if (!assetSearchQuery.trim()) return paletteSourceConnectors
    const q = assetSearchQuery.trim().toLowerCase()
    return {
      fileSystem: paletteSourceConnectors.fileSystem.filter(item => item.label.toLowerCase().includes(q)),
      database: paletteSourceConnectors.database.filter(item => item.label.toLowerCase().includes(q)),
      restApi: paletteSourceConnectors.restApi.filter(item => item.label.toLowerCase().includes(q)),
      nosql: paletteSourceConnectors.nosql.filter(item => item.label.toLowerCase().includes(q)),
    }
  }, [assetSearchQuery, paletteSourceConnectors])

  const filteredPaletteDestinationConnectors = useMemo(() => {
    if (!assetSearchQuery.trim()) return paletteDestinationConnectors
    const q = assetSearchQuery.trim().toLowerCase()
    return {
      fileSystem: paletteDestinationConnectors.fileSystem.filter(item => item.label.toLowerCase().includes(q)),
      database: paletteDestinationConnectors.database.filter(item => item.label.toLowerCase().includes(q)),
      restApi: [],
      nosql: paletteDestinationConnectors.nosql.filter(item => item.label.toLowerCase().includes(q)),
    }
  }, [assetSearchQuery, paletteDestinationConnectors])

  const filteredIntegrationItems = useMemo(() => {
    if (!assetSearchQuery.trim()) return INTEGRATION_ITEMS
    const q = assetSearchQuery.trim().toLowerCase()
    return INTEGRATION_ITEMS.filter(item => item.label.toLowerCase().includes(q))
  }, [assetSearchQuery])

  const filteredTransformationItems = useMemo(() => {
    if (!assetSearchQuery.trim()) return transformationPaletteItems
    const q = assetSearchQuery.trim().toLowerCase()
    return transformationPaletteItems.filter(item => item.label.toLowerCase().includes(q))
  }, [assetSearchQuery, transformationPaletteItems])

  // Auto-expand all groups and palette sections when searching
  useEffect(() => {
    if (assetSearchQuery.trim()) {
      setOpenGroups(['sources', 'integrations', 'transformations', 'destinations'])
      sourcePaletteUi.setFileSystemOpen(true)
      sourcePaletteUi.setDatabaseOpen(true)
      sourcePaletteUi.setRestApiOpen(true)
      sourcePaletteUi.setNosqlOpen(true)
      destinationPaletteUi.setFileSystemOpen(true)
      destinationPaletteUi.setDatabaseOpen(true)
      destinationPaletteUi.setNosqlOpen(true)
      setIntegrationsMergeOpen(true)
      setTransformationsLibraryOpen(true)
    }
  }, [assetSearchQuery])

  const selectedSourceNode = useMemo(() => {
    if (!chooseDataSourceNodeId) return null
    return graphSnapshot.nodes.find(node => node.id === chooseDataSourceNodeId) ?? null
  }, [chooseDataSourceNodeId, graphSnapshot.nodes])

  const selectedSourceNodeData = useMemo(() => {
    if (!selectedSourceNode) return null
    return selectedSourceNode.data as {
      label?: string
      titleLine?: string
      assetKey?: string
      iconImage?: string
    } | null
  }, [selectedSourceNode])

  const breadcrumbItems = useMemo(
    () => [
      { label: 'Home', to: ROUTES.AKASHIC_LANDING },
      'Akashic Data Pipeline',
      activeTab === 'assets' ? 'Assets' : 'Pipelines',
    ],
    [activeTab]
  )

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.brandMark} aria-hidden>
            <MaterialIcon name="hub" size={18} />
          </span>
          <span className={styles.brandLogo}>AKASHIC</span>
          <Separator orientation="vertical" className={styles.headerDivider} />
          <Breadcrumb items={breadcrumbItems} className={styles.headerBreadcrumb} />
        </div>
        <div className={styles.headerRight}>
          <Button type="button" variant="ghost" size="icon" aria-label="Notifications">
            <MaterialIcon name="notifications" size={22} />
          </Button>
          <img className={styles.avatar} src="https://i.pravatar.cc/56?img=12" alt="" />
        </div>
      </header>

      <Tabs
        value={activeTab}
        onValueChange={v => setActiveTab(v as 'assets' | 'pipelines')}
        className={styles.body}
      >
        <aside className={styles.sidebar}>
          <TooltipProvider delayDuration={300}>
            <div className={styles.sidebarScroll}>
              <Button
                type="button"
                variant="ghost"
                className={styles.modulesBack}
                onClick={() => navigate(ROUTES.AKASHIC_LANDING)}
              >
                <MaterialIcon name="arrow_back" size={18} />
                Modules Menu
              </Button>
              <div className={styles.sidebarDivider} />

              <div className={styles.projectsCard}>
                <button type="button" className={styles.projectsHeader}>
                  <span>Projects</span>
                  <MaterialIcon name="expand_more" size={18} />
                </button>
                <div className={styles.projectsBody}>Not Created</div>
              </div>

              <TabsList className={styles.tabsList}>
                <TabsTrigger value="assets" className={styles.tabsTrigger}>
                  Assets
                </TabsTrigger>
                <TabsTrigger value="pipelines" className={styles.tabsTrigger}>
                  Pipelines
                </TabsTrigger>
              </TabsList>

              <div className={styles.sidebarDivider} />

              {activeTab === 'assets' && (
                <div className={styles.searchRow}>
                  <div className={styles.searchInputWrap}>
                    <MaterialIcon name="search" size={16} className={styles.searchLeadingIcon} />
                    <Input
                      type="search"
                      placeholder="Search"
                      aria-label="Search assets"
                      className={cn(styles.searchField, '!h-8 !min-h-8 !py-0 !pl-8 !text-left !text-xs !leading-none')}
                      value={assetSearchQuery}
                      onChange={e => setAssetSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'assets' &&
                ASSET_GROUPS.map(group => {
                  const isOpen = openGroups.includes(group.id)
                  return (
                    <div key={group.id} className={styles.assetGroup}>
                      <button
                        type="button"
                        className={`${styles.assetGroupBtn} ${group.id === 'sources' ? styles.assetGroupBtnSources : ''}`}
                        onClick={() => toggleGroup(group.id)}
                        aria-expanded={isOpen}
                      >
                        {group.id === 'sources' ? (
                          <span className={styles.dataSourcesIconWrap}>
                            <MaterialIcon
                              name="database"
                              size={18}
                              className={styles.dataSourcesHeaderIcon}
                            />
                            <MaterialIcon name="add" size={11} className={styles.dataSourcesPlusBadge} />
                          </span>
                        ) : (
                          <MaterialIcon name={group.icon} size={18} />
                        )}
                        <span className={styles.assetGroupLabel}>{group.label}</span>
                        <MaterialIcon name={isOpen ? 'expand_less' : 'chevron_right'} size={18} />
                      </button>
                      {isOpen && group.id === 'sources' && (
                        <DataSourcePalette
                          groupId="sources"
                          palette={sourcePaletteUi}
                          connectors={filteredPaletteSourceConnectors}
                          isLoading={paletteSourceDataSourcesQuery.isPending}
                          loadError={
                            paletteSourceDataSourcesQuery.error?.message ?? null
                          }
                          onPaletteDragStart={onPaletteDragStart}
                        />
                      )}
                      {isOpen && group.id === 'destinations' && (
                        <DataSourcePalette
                          groupId="destinations"
                          palette={destinationPaletteUi}
                          connectors={filteredPaletteDestinationConnectors}
                          isLoading={paletteDestinationDataSourcesQuery.isPending}
                          loadError={
                            paletteDestinationDataSourcesQuery.error?.message ?? null
                          }
                          onPaletteDragStart={onPaletteDragStart}
                        />
                      )}
                      {isOpen && group.id === 'integrations' && (
                        <div className={styles.sourcesNested}>
                          <div className={styles.fileSystemSection}>
                            {integrationsMergeOpen ? (
                              <div className={styles.fileSystemCard}>
                                <button
                                  type="button"
                                  className={styles.fileSystemBar}
                                  onClick={() => setIntegrationsMergeOpen(false)}
                                  aria-expanded
                                >
                                  <span className={styles.integrationsIconWrap}>
                                    <MaterialIcon
                                      name="view_agenda"
                                      size={20}
                                      className={styles.fileSystemBarGlyph}
                                    />
                                    <MaterialIcon
                                      name="add"
                                      size={10}
                                      className={styles.integrationsPlusBadge}
                                    />
                                  </span>
                                  <span className={styles.fileSystemBarLabel}>Union & Join</span>
                                  <MaterialIcon
                                    name="expand_more"
                                    size={20}
                                    className={styles.fileSystemBarGlyph}
                                  />
                                </button>
                                <div className={styles.fileSystemGrid}>
                                  {filteredIntegrationItems.map(item => (
                                    <Tooltip key={item.id}>
                                      <TooltipTrigger asChild>
                                        <button
                                          type="button"
                                          className={styles.fileSystemTile}
                                          draggable
                                          onDragStart={e =>
                                            onPaletteDragStart(e, {
                                              label: item.label,
                                              groupId: group.id,
                                              connectorId: item.id,
                                              icon: item.icon,
                                            })
                                          }
                                        >
                                          <div className={styles.fileSystemTileInner}>
                                            <MaterialIcon
                                              name={item.icon}
                                              size={28}
                                              className={styles.iconTileGlyph}
                                            />
                                          </div>
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent side="top">
                                        {item.label}
                                      </TooltipContent>
                                    </Tooltip>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className={styles.fileSystemCollapsed}
                                onClick={() => setIntegrationsMergeOpen(true)}
                                aria-expanded={false}
                              >
                                <MaterialIcon name="merge" size={18} />
                                <span className={styles.fileSystemCollapsedLabel}>
                                  Union & Join
                                </span>
                                <MaterialIcon name="chevron_right" size={18} />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      {isOpen && group.id === 'transformations' && (
                        <div className={styles.sourcesNested}>
                          <div className={styles.fileSystemSection}>
                            {transformationsLibraryOpen ? (
                              <div className={styles.fileSystemCard}>
                                <button
                                  type="button"
                                  className={styles.fileSystemBar}
                                  onClick={() => setTransformationsLibraryOpen(false)}
                                  aria-expanded
                                >
                                  <MaterialIcon
                                    name="transform"
                                    size={20}
                                    className={styles.fileSystemBarGlyph}
                                  />
                                  <span className={styles.fileSystemBarLabel}>
                                    Transform library
                                  </span>
                                  <MaterialIcon
                                    name="expand_more"
                                    size={20}
                                    className={styles.fileSystemBarGlyph}
                                  />
                                </button>
                                <div
                                  className={`${styles.fileSystemGrid} ${styles.fileSystemGridTransform}`}
                                >
                                  {transformationsLoading ? (
                                    <div className={styles.loading}>Loading transformations...</div>
                                  ) : filteredTransformationItems.length === 0 ? (
                                    <div className={styles.noConfig}>{assetSearchQuery.trim() ? 'No matching transformations' : 'No transformations available'}</div>
                                  ) : (
                                    filteredTransformationItems.map(item => (
                                      <Tooltip key={item.id}>
                                        <TooltipTrigger asChild>
                                          <button
                                            type="button"
                                            className={styles.fileSystemTile}
                                            draggable
                                            onDragStart={e =>
                                              onPaletteDragStart(e, {
                                                label: item.label,
                                                groupId: group.id,
                                                connectorId: item.id,
                                                icon: item.icon,
                                                iconImage: item.iconSrc,
                                              })
                                            }
                                          >
                                            <div className={styles.fileSystemTileInner}>
                                              {item.iconSrc ? (
                                                <img
                                                  src={item.iconSrc}
                                                  alt={item.label}
                                                  className={styles.iconTileGlyph}
                                                />
                                              ) : (
                                                <MaterialIcon
                                                  name={item.icon}
                                                  size={26}
                                                  className={styles.iconTileGlyph}
                                                />
                                              )}
                                            </div>
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                          {toTitleCase(item.label)}
                                        </TooltipContent>
                                      </Tooltip>
                                    ))
                                  )}
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className={styles.fileSystemCollapsed}
                                onClick={() => setTransformationsLibraryOpen(true)}
                                aria-expanded={false}
                              >
                                <MaterialIcon name="transform" size={18} />
                                <span className={styles.fileSystemCollapsedLabel}>
                                  Transform library
                                </span>
                                <MaterialIcon name="chevron_right" size={18} />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
            </div>
          </TooltipProvider>

          <div className={styles.sidebarFooter}>
            <Button type="button" variant="ghost" className={styles.footerLink}>
              <MaterialIcon name="headset_mic" size={22} />
              Support
            </Button>
            <Button type="button" variant="ghost" className={styles.footerLink}>
              <MaterialIcon name="settings" size={22} />
              Settings
            </Button>
            <div className={styles.version}>Version 1.02.09</div>
          </div>
        </aside>

        <div className={styles.workspace}>
          <TabsContent value="assets" className={styles.tabWorkspaceContent} forceMount>
            <div className={styles.pipelineBar}>
              <div className={styles.pipelineInner}>
                {/* <PipelineActionsMenu
                  triggerClassName={styles.actionsBtn}
                  onNewPipeline={() => setActiveTab('assets')}
                />
                <Separator orientation="vertical" className={styles.vbar} /> */}
                {hasPipelineName ? (
                  <button
                    type="button"
                    className={styles.pipelineName}
                    onClick={() => setEditPipelineOpen(true)}
                    aria-label={`Edit pipeline, ${pipelineName}`}
                  >
                    <span className={styles.pipelineNameText}>{pipelineName}</span>
                    <MaterialIcon name="edit" size={16} aria-hidden />
                  </button>
                ) : (
                  <Skeleton
                    className={styles.pipelineName}
                    style={{ width: 180, height: 28 }}
                    aria-label="Pipeline name loading"
                  />
                )}
                <Separator orientation="vertical" className={styles.vbar} />
                <Button
                  type="button"
                  variant="ghost"
                  className={styles.toolBtn}
                  disabled={!isRunEnabled}
                >
                  <MaterialIcon name="check_circle" size={18} />
                  Validate
                </Button>
                {/* ── Run / inline status area ── */}
                {(() => {
                  const displayStatus = isPipelineActive && jobStatus === 'idle' ? 'connecting' : jobStatus
                  if (displayStatus === 'idle') return null

                  const JOB_STATUS_CFG = {
                    idle: { label: 'Not Started', icon: 'radio_button_unchecked', color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' },
                    connecting: { label: 'Connecting…', icon: 'sync', color: '#92400e', bg: '#fffbeb', border: '#fbbf24' },
                    initializing: { label: 'Initializing', icon: 'schedule', color: '#854d0e', bg: '#fefce8', border: '#facc15' },
                    running: { label: 'Running', icon: 'play_circle', color: '#15803d', bg: '#f0fdf4', border: '#86efac' },
                    finished: { label: 'Finished', icon: 'check_circle', color: '#1d4ed8', bg: '#eff6ff', border: '#93c5fd' },
                    failed: { label: 'Failed', icon: 'error', color: '#b91c1c', bg: '#fef2f2', border: '#fca5a5' },
                    stopped: { label: 'Stopped', icon: 'stop_circle', color: '#c2410c', bg: '#fff7ed', border: '#fdba74' },
                  } as const
                  const cfg = JOB_STATUS_CFG[displayStatus] ?? JOB_STATUS_CFG.idle
                  const isSpinning = displayStatus === 'connecting' || displayStatus === 'initializing' || displayStatus === 'running'
                  return (
                    <span
                      className={styles.inlineStatusBadge}
                      style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}
                      aria-label={`Pipeline status: ${cfg.label}`}
                    >
                      <span style={isSpinning ? { display: 'inline-flex', animation: 'spin 1.2s linear infinite' } : { display: 'inline-flex' }}>
                        <MaterialIcon name={cfg.icon} size={16} />
                      </span>
                      {cfg.label}
                    </span>
                  )
                })()}
                {!isPipelineActive && (
                  <Button
                    type="button"
                    variant="ghost"
                    className={styles.toolBtn}
                    onClick={() => void handleRunPipeline()}
                    disabled={!hasValidDataFlowBootstrap || !isRunEnabled || isRunningPipeline}
                  >
                    <MaterialIcon name="play_arrow" size={18} />
                    {isRunningPipeline ? 'Running...' : 'Run'}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  className={styles.toolBtn}
                  onClick={() => void handleStopPipeline()}
                  disabled={!isPipelineActive}
                >
                  <MaterialIcon name="stop_circle" size={18} />
                  Stop
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className={styles.toolBtn}
                  onClick={() => {
                    dispatch(
                      openGlobalOverlay({
                        type: 'schedule-pipeline-modal',
                        data: {
                          pipelineName,
                          sessionId,
                        },
                      })
                    )
                  }}
                  disabled={isInitializingPipeline || isRunningPipeline || isPipelineActive || !isRunEnabled}
                >
                  <MaterialIcon name="calendar_today" size={18} />
                  Schedule
                </Button>
                <div className={styles.spacer} />
                {/* Right-side status pill */}
                {(() => {
                  const displayStatus = isPipelineActive && jobStatus === 'idle' ? 'connecting' : jobStatus
                  const STATUS_PILL_CFG = {
                    idle: { label: 'Not Started', dotColor: '#9ca3af', pillBg: '#f3f4f6', pillBorder: '#99a1af', pillText: '#6a7282' },
                    connecting: { label: 'Connecting…', dotColor: '#f59e0b', pillBg: '#fffbeb', pillBorder: '#fbbf24', pillText: '#92400e' },
                    initializing: { label: 'Initializing', dotColor: '#eab308', pillBg: '#fefce8', pillBorder: '#facc15', pillText: '#854d0e' },
                    running: { label: 'Running', dotColor: '#22c55e', pillBg: '#f0fdf4', pillBorder: '#86efac', pillText: '#15803d' },
                    finished: { label: 'Finished', dotColor: '#3b82f6', pillBg: '#eff6ff', pillBorder: '#93c5fd', pillText: '#1d4ed8' },
                    failed: { label: 'Failed', dotColor: '#ef4444', pillBg: '#fef2f2', pillBorder: '#fca5a5', pillText: '#b91c1c' },
                    stopped: { label: 'Stopped', dotColor: '#f97316', pillBg: '#fff7ed', pillBorder: '#fdba74', pillText: '#c2410c' },
                  } as const
                  const cfg = STATUS_PILL_CFG[displayStatus] ?? STATUS_PILL_CFG.idle
                  const isAnimated = displayStatus === 'running' || displayStatus === 'initializing' || displayStatus === 'connecting'
                  return (
                    <Badge
                      variant="outline"
                      className={styles.statusPill}
                      style={{ background: cfg.pillBg, borderColor: cfg.pillBorder, color: cfg.pillText }}
                    >
                      <span
                        className={styles.statusDot}
                        style={{
                          background: cfg.dotColor,
                          boxShadow: isAnimated ? `0 0 0 3px ${cfg.dotColor}40` : undefined,
                          animation: isAnimated ? 'pulseDot 1.4s ease-in-out infinite' : undefined,
                        }}
                      />
                      {cfg.label}
                    </Badge>
                  )
                })()}
                <Separator orientation="vertical" className={styles.vbar} />
                {/* <Button
                  type="button"
                  variant="ghost"
                  className={styles.pipelineDetailsBtn}
                  aria-label="Open pipeline run details"
                  aria-expanded={pipelineDetailsOpen}
                  onClick={() => {
                    setRunHistoryOpen(false)
                    closeDataSourceFlow()
                    closeIntegrationSettings()
                    setPipelineDetailsOpen(true)
                  }}
                >
                  <MaterialIcon name="info" size={18} />
                </Button> */}
                <PipelineMoreMenu
                  triggerClassName={styles.iconBtn}
                  onLockPipeline={handleLockPipeline}
                  onClearCanvas={handleClearCanvas}
                  onDeletePipeline={handleDeletePipeline}
                  disableCanvasActions={!hasCanvasNodes}
                />
              </div>
            </div>

            <div className={styles.mainStage}>
              <ChooseDataSourceContext.Provider value={chooseDataSourceContextValue}>
                <div className={styles.canvas}>
                  <AdpPipelineFlowCanvas
                    onGraphChange={handleGraphChange}
                    onBridgeReady={handlePipelineGraphBridgeReady}
                    canEditPipeline={hasValidDataFlowBootstrap}
                    hydrationSnapshot={hydrationSnapshot}
                    hydrationKey={hydrationKey}
                    onRequirePipelineSetup={requirePipelineSetup}
                  />
                </div>
              </ChooseDataSourceContext.Provider>
              {runHistoryOpen && (
                <div className={styles.runHistoryOverlay}>
                  <RunHistoryPanel onClose={() => setRunHistoryOpen(false)} />
                </div>
              )}
              {!runHistoryOpen &&
                pipelineDetailsOpen &&
                !chooseDataSourceOpen &&
                !integrationSettings && (
                  <PipelineDetailsSheet
                    onClose={() => setPipelineDetailsOpen(false)}
                    pipelineName={pipelineName}
                    onViewRunHistory={() => setRunHistoryOpen(true)}
                  />
                )}
              {!runHistoryOpen && integrationSettings && (
                <JoinDetailsSheet
                  configuringNodeId={integrationSettings.nodeId}
                  connectorId={integrationSettings.connectorId}
                  upstreamSourceNodeIds={integrationUpstreamSourceIds}
                  graphBridgeRef={pipelineGraphBridgeRef}
                  onClose={closeIntegrationSettings}
                />
              )}
              {!runHistoryOpen && transformationSettings && (
                <ConfigureTransformationSheet
                  configuringNodeId={transformationSettings.nodeId}
                  connectorId={transformationSettings.connectorId}
                  onClose={closeTransformationSettings}
                />
              )}
              {!runHistoryOpen && chooseDataSourceOpen && dataSourceSheetStep === 'choose' && (
                chooseDataSourceAssetKind === 'destination' ? (
                  <ChooseDataDestinationSheet
                    configuringNodeId={chooseDataSourceNodeId}
                    allowedInstance={chooseDataSourceAllowedInstance}
                    destinations={registeredTargetDataSourcesQuery.data}
                    isLoading={registeredTargetDataSourcesQuery.isPending}
                    loadError={registeredTargetDataSourcesQuery.error?.message ?? null}
                    selectedDateFilter={chooseDataSourceDateFilter}
                    onDateFilterChange={setChooseDataSourceDateFilter}
                    searchQuery={chooseDataSourceSearchQuery}
                    onSearchQueryChange={setChooseDataSourceSearchQuery}
                    onClose={closeDataSourceFlow}
                    onSelectDestination={handleSelectExistingSource}
                    onAddNewDestination={() => {
                      setSelectedRegisteredSource(null)
                      setDataSourceSheetStep('configure')
                      // Clear stored selection when adding new destination
                      if (chooseDataSourceNodeId) {
                        removeSelectedRegisteredSource(chooseDataSourceNodeId)
                      }
                    }}
                  />
                ) : (
                  <ChooseDataSourceSheet
                    configuringNodeId={chooseDataSourceNodeId}
                    sources={registeredSourceDataSourcesQuery.data}
                    isLoading={registeredSourceDataSourcesQuery.isPending}
                    loadError={registeredSourceDataSourcesQuery.error?.message ?? null}
                    selectedDateFilter={chooseDataSourceDateFilter}
                    onDateFilterChange={setChooseDataSourceDateFilter}
                    searchQuery={chooseDataSourceSearchQuery}
                    onSearchQueryChange={setChooseDataSourceSearchQuery}
                    onClose={closeDataSourceFlow}
                    onSelectSource={handleSelectExistingSource}
                    onAddNewSource={() => {
                      setSelectedRegisteredSource(null)
                      setDataSourceSheetStep('configure')
                      // Clear stored selection when adding new source
                      if (chooseDataSourceNodeId) {
                        removeSelectedRegisteredSource(chooseDataSourceNodeId)
                      }
                    }}
                  />
                )
              )}
              {!runHistoryOpen && chooseDataSourceOpen && dataSourceSheetStep === 'configure' && (
                <div className={styles.runHistoryOverlay}>
                  {chooseDataSourceAssetKind === 'destination' ? (
                    <ConfigureDataDestinationSheet
                      dataFlowId={dataFlowId}
                      configuringNodeId={chooseDataSourceNodeId}
                      allowedInstance={chooseDataSourceAllowedInstance}
                      destinationTitle={selectedSourceNodeData?.titleLine ?? selectedSourceNodeData?.label}
                      destinationTypeLabel={selectedSourceNodeData?.assetKey}
                      connectorImage={selectedSourceNodeData?.iconImage}
                      graphSnapshot={graphSnapshot}
                      onSavePipelineDefinition={saveDestinationChanges}
                      onColumnMappingSaved={() => dispatch(setRunEnabled(true))}
                      onClose={closeDataSourceFlow}
                      onBackToChoose={() => {
                        setDataSourceSheetStep('choose')
                        setSelectedRegisteredSource(null)
                        // Clear stored selection when going back to choose
                        if (chooseDataSourceNodeId) {
                          removeSelectedRegisteredSource(chooseDataSourceNodeId)
                        }
                      }}
                      registeredDestination={selectedRegisteredSource}
                    />
                  ) : (
                    <ConfigureDataSourceSheet
                      configuringNodeId={chooseDataSourceNodeId}
                      allowedInstance={chooseDataSourceAllowedInstance}
                      sourceTitle={selectedSourceNodeData?.titleLine ?? selectedSourceNodeData?.label}
                      sourceTypeLabel={selectedSourceNodeData?.assetKey}
                      connectorImage={selectedSourceNodeData?.iconImage}
                      onClose={closeDataSourceFlow}
                      onBackToChoose={() => {
                        setDataSourceSheetStep('choose')
                        setSelectedRegisteredSource(null)
                        // Clear stored selection when going back to choose
                        if (chooseDataSourceNodeId) {
                          removeSelectedRegisteredSource(chooseDataSourceNodeId)
                        }
                      }}
                      registeredSource={selectedRegisteredSource}
                    />
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="pipelines" className={styles.tabWorkspaceContent} forceMount>
            <div className={styles.mainStage}>
              <PipelinesListView
                isActive={activeTab === 'pipelines'}
                onNewPipeline={handleNewPipeline}
                onOpenPipeline={pipelineId => void handleOpenPipelineFromList(pipelineId)}
                onRunImmediate={handleRunImmediateList}
                onStopPipeline={handleStopPipelineFromList}
                liveStatuses={liveStatuses}
                liveMessages={liveMessages}
              />
            </div>
          </TabsContent>
        </div>
      </Tabs>

      <EditPipelineModal
        open={editPipelineOpen}
        onOpenChange={setEditPipelineOpen}
      />
      {/* For testing force : forceShow={true} */}
      <GuidedTour />

    </div>
  )
}
